import { inject, Injectable } from '@angular/core';
import { EntityState } from '@ngrx/entity';

import {
  applyPropsPatch,
  CanvasElement,
  CanvasElementFrom,
  CanvasNamespaceId,
  ColumnId,
  getRootElementId,
  isNavMenuItemsLayout,
  LayoutData,
  NavigationContainerVariantType,
  PartialPropsOf,
  PropsOf,
} from '@blaze-runtime/engine-domain';
import { PageInfo } from '@blaze-shared/api-contracts-domain';
import { NavMenu } from '@blaze-shared/elements-domain';
import { NavigationPageType, PageCreationData } from '@blaze-shared/projects-domain';
import { toInt } from '@blaze-shared/shared-util';
import { fromStyles } from '@blaze-shared/style-state-feature';
import { ThemesService } from '@blaze-shared/theming-data-access';
import { StyleSheet } from '@blaze-shared/theming-domain';

import { CanvasElementDefinitionInternal } from '../canvas-elements/element-definition';
import { isSlotIdOfContainer } from '../canvas-elements/element-model/element-props-guards';
import { isElementType } from '../canvas-elements/element-model/element-type-guards';
import { RootElement } from '../elements/root/root.element';
import {
  ROOT_BOTTOM_SLOT,
  ROOT_HEADER_SLOT,
  ROOT_MAIN_SLOT,
  ROOT_SIDE_SLOT,
} from '../elements/root/root.models';
import { elementsToEntityState, entityStateToElements } from '../utils/elements/element.utils';
import { PlaceElementData, placeElementMutably } from '../utils/layout';
import { getPathFirstInSlot } from '../utils/layout/helpers';
import { insertColumnMutably } from '../utils/layout/insert-column';
import { setSlotAlignmentMutably } from '../utils/layout/set-slot-alignment';

import { ElementDefinitionsService } from './element-definitions.service';
import { setStyleSheetToElementMutably } from './styles';

const NAV_HEADER_CONTAINER_STYLE_NAME = 'Navigation Header';
const NAV_SIDEBAR_CONTAINER_STYLE_NAME = 'Navigation Sidebar';
const NAV_MENU_HEADER_STYLE_NAME = 'Nav Menu - Header';
const NAV_MENU_SIDEBAR_STYLE_NAME = 'Nav Menu - Sidebar';
const IMAGE_SIDEBAR_STYLE_NAME = 'Sidebar Image';
const IMAGE_HEADER_STYLE_NAME = 'Header Image';
const NAV_HEADER_IMAGE_CONTAINER_STYLE_NAME = 'Header Image Container';
const NAV_SIDEBAR_IMAGE_CONTAINER_STYLE_NAME = 'Sidebar Image Container';

const DEFAULT_HEADER_NAV_MENU_INIT_ITEMS: NavMenu.InitItem[] = [
  { type: 'shared', props: { label: 'Menu Item 1', icon: 'fa-star' } },
  { type: 'shared', props: { label: 'Menu Item 2', icon: 'fa-envelope' } },
  {
    type: 'shared',
    props: { label: 'Menu Item 3', icon: 'fas-magnifying-glass' },
    children: [
      { type: 'shared', props: { label: 'Sub-Menu Item 1' } },
      { type: 'shared', props: { label: 'Sub-Menu Item 2' } },
    ],
  },
  { type: 'shared', props: { label: 'Menu Item 4', icon: 'fas-chart-line' } },
];

const DEFAULT_SIDEBAR_NAV_MENU_INIT_ITEMS: NavMenu.InitItem[] = [
  { type: 'shared', props: { label: 'Menu Item 1', icon: 'fa-star' } },
  { type: 'shared', props: { label: 'Menu Item 2', icon: 'fa-envelope' } },
  {
    type: 'section',
    props: { label: 'Section' },
    children: [
      {
        type: 'shared',
        props: { label: 'Menu Item 3', icon: 'fas-magnifying-glass' },
        children: [
          { type: 'shared', props: { label: 'Sub-Menu Item 1' } },
          { type: 'shared', props: { label: 'Sub-Menu Item 2' } },
        ],
      },
      { type: 'shared', props: { label: 'Menu Item 4', icon: 'fas-chart-line' } },
    ],
  },
];

@Injectable({ providedIn: 'root' })
export class LayoutGenerationService {
  private readonly elementDefinitionService = inject(ElementDefinitionsService);
  private readonly themesService = inject(ThemesService);

  normalizeLayout(layout: LayoutData, page?: PageInfo): CanvasElement[] {
    // Skip layout initialization for nav menu items storage
    if (isNavMenuItemsLayout(layout) || page?.pageType === 'NavMenus') {
      return [];
    }

    // Layout has been initialized, passthrough
    if (layout.uiLayer.content.length > 0) {
      return layout.uiLayer.content;
    }

    // Every layout should have a default layout created
    return this.generateDefaultLayout(layout.pageId);
  }

  generateRootElement(
    namespaceId: CanvasNamespaceId,
    initProps?: PartialPropsOf<'root'>,
  ): RootElement {
    const rootDefinition = this.elementDefinitionService.getDefinitionSafe('root');

    return {
      id: getRootElementId(namespaceId),
      ns: namespaceId,
      name: 'Root Element',
      width: { mode: 'full' },
      height: { mode: 'full' },
      type: 'root',
      version: rootDefinition.version,
      slots: {
        ids: [ROOT_MAIN_SLOT],
        [ROOT_MAIN_SLOT]: {
          children: [],
          sizing: {},
        },
      },
      props: { ...initProps },
    } as RootElement;
  }

  generateDefaultLayout(namespaceId: CanvasNamespaceId): CanvasElement[] {
    return [this.generateRootElement(namespaceId)] as CanvasElement[];
  }

  generateDefaultNavigationPageLayout(
    namespaceId: CanvasNamespaceId,
    pageType: NavigationPageType,
    creationData?: PageCreationData,
    styleSheets?: StyleSheet[],
  ): CanvasElement[] {
    if (pageType === 'Header') {
      return this.createDefaultNavHeaderLayout(namespaceId, creationData, styleSheets);
    }

    if (pageType === 'Sidebar') {
      return this.createDefaultNavSidebarLayout(namespaceId, creationData, styleSheets);
    }

    if (pageType === 'Bottombar') {
      return this.createDefaultNavBottombarLayout(namespaceId, styleSheets);
    }

    console.warn('Unhandled navigation page type found', { pageType });
    return [];
  }

  generateLayoutWithCreationData(
    namespaceId: CanvasNamespaceId,
    pageCreation: PageCreationData,
  ): CanvasElement[] {
    const root = this.generateRootElement(namespaceId);
    const entityState = elementsToEntityState([root]);
    const { navigationLayout, navigationElements } = pageCreation;

    if (
      navigationLayout === 'Top' ||
      navigationLayout === 'Mixed' ||
      navigationLayout === 'TopAndFooter' ||
      navigationElements?.includes('Header')
    ) {
      const navContainerDefinition =
        this.elementDefinitionService.getDefinitionSafe('navContainer');
      placeElementMutably(entityState, {
        elementDefinition: navContainerDefinition,
        initProps: navContainerDefinition.initial,
        atPath: {
          id: root.id,
          slot: ROOT_HEADER_SLOT,
          parentId: root.id,
          index: 0,
          targetPosition: {
            type: 'new-root-slot',
          },
        },
      });
    }

    if (
      navigationLayout === 'Side' ||
      navigationLayout === 'Mixed' ||
      navigationElements?.includes('Sidebar')
    ) {
      const navSidebarDefinition = this.elementDefinitionService.getDefinitionSafe('navSidebar');
      placeElementMutably(entityState, {
        elementDefinition: navSidebarDefinition,
        initProps: navSidebarDefinition.initial,
        atPath: {
          id: root.id,
          slot: ROOT_SIDE_SLOT,
          parentId: root.id,
          index: 0,
          targetPosition: {
            type: 'new-root-slot',
          },
        },
      });
    }

    if (navigationLayout === 'TopAndFooter' || navigationElements?.includes('Bottombar')) {
      const navBottombarDefinition =
        this.elementDefinitionService.getDefinitionSafe('navBottombar');
      placeElementMutably(entityState, {
        elementDefinition: navBottombarDefinition,
        initProps: navBottombarDefinition.initial,
        atPath: {
          id: root.id,
          slot: ROOT_BOTTOM_SLOT,
          parentId: root.id,
          index: 0,
          targetPosition: {
            type: 'new-root-slot',
          },
        },
      });
    }

    return entityStateToElements(entityState);
  }

  private createInitialStateForNavElement<T extends NavigationContainerVariantType>(
    namespaceId: CanvasNamespaceId,
    type: T,
    styleSheets: StyleSheet[] = [],
    desiredStyleName?: string,
  ): {
    entityState: EntityState<CanvasElement>;
    mainContainer: CanvasElementFrom<T>;
    firstColumnId: ColumnId;
  } {
    const root = this.generateRootElement(namespaceId);
    const entityState = elementsToEntityState([root]);
    const atPath = getPathFirstInSlot(entityState, root.id, 'main');

    const combinedInitProps: PartialPropsOf<'container'> = {
      style: {
        container: { offset: { padding: '$t.spacingNavContainer' } },
      },
    };

    const mainContainerId = placeElementMutably(entityState, {
      elementDefinition: this.elementDefinitionService.getDefinitionSafe(type),
      initProps: combinedInitProps,
      atPath,
      style: {
        name: desiredStyleName,
        styleSheets,
        useDefault: true,
      },
    })?.elementId;

    const mainContainer = entityState.entities[mainContainerId ?? ''];
    if (!isElementType(mainContainer, type)) {
      throw new Error('Failed to create main container.');
    }

    const firstColumnId = mainContainer.slots?.ids?.[0];
    if (!isSlotIdOfContainer(mainContainer, firstColumnId)) {
      throw new Error('First column of main container was not found');
    }

    return { entityState, mainContainer, firstColumnId };
  }

  /**
   * Creates the default layout for the nav header with proper variant.
   * @param namespaceId
   * @param creationData
   * @param styleSheets
   * @private
   */
  private createDefaultNavHeaderLayout(
    namespaceId: CanvasNamespaceId,
    creationData?: PageCreationData,
    styleSheets: StyleSheet[] = [],
  ): CanvasElement[] {
    if (creationData?.navigationLayout === 'Mixed') {
      return this.createDefaultNavHeaderMixedVariant(namespaceId, creationData, styleSheets);
    }

    return this.createDefaultNavHeaderLayoutStandardVariant(namespaceId, creationData, styleSheets);
  }

  /**
   * Standard nav header layout is
   * - logo, on the left
   * - nav menu, in middle
   * - avatar, on the right
   * @param namespaceId
   * @param creationData
   * @param styleSheets
   * @private
   */
  private createDefaultNavHeaderLayoutStandardVariant(
    namespaceId: CanvasNamespaceId,
    creationData?: PageCreationData,
    styleSheets: StyleSheet[] = [],
  ): CanvasElement[] {
    const { entityState, mainContainer, firstColumnId } = this.createInitialStateForNavElement(
      namespaceId,
      'navHeaderContainer',
      styleSheets,
      NAV_HEADER_CONTAINER_STYLE_NAME,
    );

    const secondColumnId = insertColumnMutably(
      entityState,
      mainContainer.id,
      firstColumnId,
      'after-slot',
      {
        mode: 'auto',
      },
    ).slotId;

    const firstColumn = mainContainer.slots?.[firstColumnId];
    const secondColumn = secondColumnId ? mainContainer.slots?.[secondColumnId] : undefined;

    if (!firstColumn || !secondColumn || !isSlotIdOfContainer(mainContainer, secondColumnId)) {
      throw new Error('Failed to setup columns for generated layout.');
    }

    const navHeaderContainerDefinition =
      this.elementDefinitionService.getDefinitionSafe('navHeaderContainer');
    firstColumn.props = applyPropsPatch<'container'>(
      navHeaderContainerDefinition.slotInitial || {},
      {
        style: {
          container: {
            offset: { padding: '0px' },
          },
        },
      },
    );
    firstColumn.sizing = { ...firstColumn.sizing, mode: 'fit-content' };
    secondColumn.props = applyPropsPatch<'container'>(
      navHeaderContainerDefinition.slotInitial || {},
      {
        style: {
          container: {
            offset: { padding: '0px 16px 0px 16px' },
          },
        },
      },
    );

    setSlotAlignmentMutably(entityState, mainContainer.id, firstColumnId, 'left', 'middle');
    setSlotAlignmentMutably(entityState, mainContainer.id, secondColumnId, 'left', 'middle');

    const imageDefinition = this.elementDefinitionService.getDefinitionSafe('image');
    const navMenuDefinition = this.elementDefinitionService.getDefinitionSafe('navMenu');

    const avatarDefinition = this.elementDefinitionService.getDefinitionSafe('avatar');
    const containerDefinition = this.elementDefinitionService.getDefinitionSafe('container');

    const imageResult = placeElementMutably(entityState, {
      elementDefinition: imageDefinition,
      initProps: applyPropsPatch(imageDefinition.initial, {
        content: {
          imageSource: {
            placeholder: {
              fileName: '$t.phLogoTopFileName',
              width: '$t.phLogoTopWidth',
              height: '$t.phLogoTopHeight',
            },
          },
          options: {
            behavior: 'contain',
          },
        },
      }),
      atPath: {
        id: mainContainer.id,
        parentId: mainContainer.id,
        slot: firstColumnId,
        index: 0,
        targetPosition: {
          type: 'top-new-container',
        },
      },
      desiredSize: {
        width: { mode: 'full' },
        height: { mode: 'auto' },
      },
      style: {
        name: IMAGE_HEADER_STYLE_NAME,
        styleSheets,
        useDefault: false,
      },
    });
    const image = entityState.entities[imageResult?.elementId ?? ''];
    const imageContainer = entityState.entities[image?.parentNode?.id ?? ''];
    if (isElementType(imageContainer, 'container')) {
      imageContainer.props = applyPropsPatch(
        containerDefinition.initial || {},
        {},
      ) as PropsOf<'container'>;

      setSlotAlignmentMutably(
        entityState,
        imageContainer.id,
        imageContainer.slots.ids[0],
        'left',
        'middle',
      );

      const imageContainerStyleSheet = fromStyles.findMatchingStyleSheet({
        name: NAV_HEADER_IMAGE_CONTAINER_STYLE_NAME,
        elementType: containerDefinition.type,
        sheets: styleSheets,
      });
      if (imageContainerStyleSheet) {
        setStyleSheetToElementMutably({
          entities: entityState.entities,
          elementId: imageContainer.id,
          elementDefinition: containerDefinition,
          styleSheet: imageContainerStyleSheet,
        });
      }
    }

    placeElementMutably(entityState, {
      elementDefinition: navMenuDefinition,
      initProps: applyPropsPatch(navMenuDefinition.initial, {
        content: {
          initialItems: creationData?.menuPages?.length
            ? this.createInitMenuItemsFromPages(creationData.menuPages)
            : DEFAULT_HEADER_NAV_MENU_INIT_ITEMS,
          markSelected: false,
        },
      }),
      atPath: {
        id: mainContainer.id,
        parentId: mainContainer.id,
        slot: secondColumnId,
        index: 0,
      },
      style: {
        name: NAV_MENU_HEADER_STYLE_NAME,
        styleSheets,
        useDefault: true,
      },
    });

    if (creationData?.addAccountsDropdown) {
      this.appendAccountDropdownToNavHeader(entityState, mainContainer, styleSheets);
    }
    const avatarColumnId = this.appendColumnToNavHeader(entityState, mainContainer);
    if (!avatarColumnId) {
      throw new Error('Failed to append avatar to nav header, column could not be created');
    }

    placeElementMutably(entityState, {
      elementDefinition: avatarDefinition,
      initProps: applyPropsPatch(avatarDefinition.initial, {
        content: {
          fields: {
            name: 'Name',
            image: 'ProfilePicture',
          },
        },
        style: {
          layout: {
            imageSize: '48px',
            imagePosition: 'right',
            labelAlign: {
              horizontal: 'left',
            },
          },
        },
      }),
      atPath: {
        id: mainContainer.id,
        parentId: mainContainer.id,
        slot: avatarColumnId,
        index: 0,
      },
      desiredSize: {
        width: { mode: 'value', value: 200, fixed: false },
      },
      style: {
        styleSheets,
        useDefault: true,
      },
    });

    return entityStateToElements(entityState);
  }

  private appendAccountsDropdown(
    entityState: EntityState<CanvasElement>,
    columnId: ColumnId,
    containerId: string,
    styleSheets: StyleSheet[],
    overrides?: Partial<PlaceElementData>,
  ): void {
    const accountsDropdownDefinition =
      this.elementDefinitionService.getDefinitionSafe('accountsDropdown');

    placeElementMutably(entityState, {
      elementDefinition: accountsDropdownDefinition,
      initProps: accountsDropdownDefinition.initial,
      atPath: {
        id: containerId,
        parentId: containerId,
        slot: columnId,
        index: 0,
      },
      desiredSize: {
        width: { mode: 'full' },
      },
      style: {
        styleSheets,
        useDefault: true,
      },
      ...overrides,
    });
  }

  private appendAccountDropdownToNavHeader(
    entityState: EntityState<CanvasElement>,
    mainContainer: CanvasElement,
    styleSheets: StyleSheet[],
  ): void {
    const accountsDropdownColumnId = this.appendColumnToNavHeader(entityState, mainContainer);
    if (!accountsDropdownColumnId) {
      throw new Error(
        'Failed to append accounts dropdown to nav header, column could not be created',
      );
    }
    this.appendAccountsDropdown(
      entityState,
      accountsDropdownColumnId,
      mainContainer.id,
      styleSheets,
      {
        desiredSize: {
          width: { mode: 'value', value: 260, fixed: true },
        },
      },
    );
  }

  private appendColumnToNavHeader(
    entityState: EntityState<CanvasElement>,
    container: CanvasElement,
  ): ColumnId | undefined {
    if (!container.slots) {
      return;
    }
    const lastColumnId = container.slots.ids[container.slots.ids.length - 1];

    if (!isSlotIdOfContainer(container, lastColumnId)) {
      return;
    }
    const newColumnId = insertColumnMutably(entityState, container.id, lastColumnId, 'after-slot', {
      mode: 'fit-content',
    }).slotId;

    if (!newColumnId) {
      return;
    }

    const containerDefinition = this.elementDefinitionService.getDefinitionSafe(
      container.type,
    ) as CanvasElementDefinitionInternal<'navHeaderContainer' | 'navSidebarContainer'>;

    const newColumn = container.slots?.[newColumnId];
    if (!newColumn) {
      return;
    }
    newColumn.props = applyPropsPatch<'container'>(containerDefinition.slotInitial || {}, {
      style: {
        container: {
          offset: { padding: '0px 12px 0px 0px' },
        },
      },
    });
    setSlotAlignmentMutably(entityState, container.id, newColumnId, 'right', 'middle');
    return newColumnId;
  }

  /**
   * Mixed nav header layout is
   * - avatar, on the right
   * @param namespaceId
   * @param creationData
   * @param styleSheets
   * @private
   */
  private createDefaultNavHeaderMixedVariant(
    namespaceId: CanvasNamespaceId,
    creationData?: PageCreationData,
    styleSheets: StyleSheet[] = [],
  ): CanvasElement[] {
    const { entityState, mainContainer, firstColumnId } = this.createInitialStateForNavElement(
      namespaceId,
      'navHeaderContainer',
      styleSheets,
      NAV_HEADER_CONTAINER_STYLE_NAME,
    );

    const firstColumn = mainContainer.slots?.[firstColumnId];

    if (!firstColumn) {
      throw new Error('Failed to setup columns for generated layout.');
    }

    setSlotAlignmentMutably(entityState, mainContainer.id, firstColumnId, 'left', 'middle');

    const avatarDefinition = this.elementDefinitionService.getDefinitionSafe('avatar');

    if (creationData?.addAccountsDropdown) {
      this.appendAccountDropdownToNavHeader(entityState, mainContainer, styleSheets);
    }

    const avatarColumnId = this.appendColumnToNavHeader(entityState, mainContainer);
    if (!avatarColumnId) {
      throw new Error('Failed to append avatar to nav header, column could not be created');
    }
    placeElementMutably(entityState, {
      elementDefinition: avatarDefinition,
      initProps: applyPropsPatch(avatarDefinition.initial, {
        content: {
          fields: {
            name: 'Name',
            image: 'ProfilePicture',
          },
        },
        style: {
          layout: {
            imageSize: '48px',
            imagePosition: 'right',
            labelAlign: {
              horizontal: 'left',
            },
          },
        },
      }),
      atPath: {
        id: mainContainer.id,
        parentId: mainContainer.id,
        slot: avatarColumnId,
        index: 0,
      },
      desiredSize: {
        width: { mode: 'value', value: 200, fixed: false },
      },
      style: {
        styleSheets,
        useDefault: true,
      },
    });

    return entityStateToElements(entityState);
  }

  /**
   * Sidebar layout is always the same, but in the mixed variant we want it to be stretched on top of nav header and bottombar.
   * @param namespaceId
   * @param creationData
   * @param styleSheets
   * @private
   */
  private createDefaultNavSidebarLayout(
    namespaceId: CanvasNamespaceId,
    creationData?: PageCreationData,
    styleSheets: StyleSheet[] = [],
  ): CanvasElement[] {
    const { entityState, mainContainer, firstColumnId } = this.createInitialStateForNavElement(
      namespaceId,
      'navSidebarContainer',
      styleSheets,
      NAV_SIDEBAR_CONTAINER_STYLE_NAME,
    );

    /**
     * For mixed layout, we need to make the sidebar full height.
     */
    if (creationData?.navigationLayout === 'Mixed') {
      mainContainer.props = applyPropsPatch(mainContainer.props ?? {}, {
        content: { isFullHeight: true },
      }) as PropsOf<'navSidebarContainer'>;
    }

    const imageDefinition = this.elementDefinitionService.getDefinitionSafe('image');
    const navMenuDefinition = this.elementDefinitionService.getDefinitionSafe('navMenu');
    const containerDefinition = this.elementDefinitionService.getDefinitionSafe('container');
    const hasHeaderNavigation = this.hasHeaderNavigation(creationData);
    const shouldAddAccountsDropdownInSidebar =
      !!creationData?.addAccountsDropdown && !hasHeaderNavigation;

    const spacingValue = toInt(this.themesService.activeTheme?.value?.spacingNavContainer ?? '');
    mainContainer.width = { mode: 'value', value: 240 + spacingValue * 4, fixed: false };

    const imageResult = placeElementMutably(entityState, {
      elementDefinition: imageDefinition,
      initProps: applyPropsPatch(imageDefinition.initial, {
        content: {
          imageSource: {
            placeholder: {
              fileName: '$t.phLogoSideFileName',
              width: '$t.phLogoSideWidth',
              height: '$t.phLogoSideHeight',
            },
          },
          markSelected: false,
          options: {
            behavior: 'contain',
          },
        },
      }),
      atPath: {
        id: mainContainer.id,
        parentId: mainContainer.id,
        slot: firstColumnId,
        index: 0,
        targetPosition: {
          type: 'top-new-container',
        },
      },
      desiredSize: {
        width: { mode: 'full' },
        height: { mode: 'auto' },
      },
      style: {
        name: IMAGE_SIDEBAR_STYLE_NAME,
        styleSheets,
        useDefault: false,
      },
    });
    const image = entityState.entities[imageResult?.elementId ?? ''];
    const imageContainer = entityState.entities[image?.parentNode?.id ?? ''];
    if (isElementType(imageContainer, 'container')) {
      imageContainer.props = applyPropsPatch(
        containerDefinition.initial || {},
        {},
      ) as PropsOf<'container'>;

      setSlotAlignmentMutably(
        entityState,
        imageContainer.id,
        imageContainer.slots.ids[0],
        'center',
        'middle',
      );

      const imageContainerStyleSheet = fromStyles.findMatchingStyleSheet({
        name: NAV_SIDEBAR_IMAGE_CONTAINER_STYLE_NAME,
        elementType: containerDefinition.type,
        sheets: styleSheets,
      });
      if (imageContainerStyleSheet) {
        setStyleSheetToElementMutably({
          entities: entityState.entities,
          elementId: imageContainer.id,
          elementDefinition: containerDefinition,
          styleSheet: imageContainerStyleSheet,
        });
      }
    }

    let navMenuIndex = 1;
    if (shouldAddAccountsDropdownInSidebar) {
      this.appendAccountsDropdown(entityState, firstColumnId, mainContainer.id, styleSheets, {
        atPath: { id: mainContainer.id, index: 1, parentId: mainContainer.id, slot: firstColumnId },
      });

      navMenuIndex = 2;
    }

    placeElementMutably(entityState, {
      elementDefinition: navMenuDefinition,
      initProps: applyPropsPatch(navMenuDefinition.initial, {
        content: {
          initialItems: creationData?.menuPages?.length
            ? this.createInitMenuItemsFromPages(creationData.menuPages)
            : DEFAULT_SIDEBAR_NAV_MENU_INIT_ITEMS,
          markSelected: false,
          sections: {
            showDivider: true,
          },
        },
      }),
      atPath: {
        id: mainContainer.id,
        parentId: mainContainer.id,
        slot: firstColumnId,
        index: navMenuIndex,
      },
      desiredSize: {
        width: { mode: 'full' },
      },
      style: {
        name: NAV_MENU_SIDEBAR_STYLE_NAME,
        styleSheets,
        useDefault: true,
      },
    });

    return entityStateToElements(entityState);
  }

  private hasHeaderNavigation(creationData?: PageCreationData): boolean {
    return (
      creationData?.navigationLayout === 'Top' ||
      creationData?.navigationLayout === 'Mixed' ||
      creationData?.navigationLayout === 'TopAndFooter' ||
      !!creationData?.navigationElements?.includes('Header')
    );
  }

  /**
   * Create an empty bottombar container.
   * @param namespaceId
   * @param styleSheets
   * @private
   */
  private createDefaultNavBottombarLayout(
    namespaceId: CanvasNamespaceId,
    styleSheets: StyleSheet[] = [],
  ): CanvasElement[] {
    const { entityState } = this.createInitialStateForNavElement(
      namespaceId,
      'navBottombarContainer',
      styleSheets,
    );

    return entityStateToElements(entityState);
  }

  private createInitMenuItemsFromPages(pages: PageInfo[]): NavMenu.InitItem[] {
    return pages.map((page) => ({
      type: 'shared',
      props: {
        label: page.name,
        clickAction: 'GoToPage',
        actionParams: page.id,
      },
    }));
  }
}
